import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, CalendarClock, Video, Copy, CheckCircle2, XCircle, Hourglass, Clock, Ban, RefreshCw, ExternalLink, CalendarPlus } from 'lucide-react';
import { interviewsAPI } from '@/utils/api';
import { prospectDate } from '@/utils/prospectTime';
import { useAuth } from '@/contexts/AuthContext';
import { ScheduleAudit } from '@/components/interviews/ScheduleAudit';

const fmt = (iso, lang) => iso ? prospectDate(iso).toLocaleString(lang === 'en' ? 'en-GB' : lang === 'fr' ? 'fr-FR' : 'pt-PT', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Atlantic/Cape_Verde' }) : '—';

/** Gera URL para criar evento no Google Calendar sem precisar de credenciais. */
function googleCalendarUrl(iv) {
  if (!iv.scheduled_start) return null;
  const start = new Date(iv.scheduled_start);
  const end = new Date(start.getTime() + (iv.duration_min || 30) * 60000);
  const fmt8 = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const title = encodeURIComponent(`Entrevista Trabadju — ${iv.candidate_name} · ${iv.job_title}`);
  const details = encodeURIComponent(
    `Candidato: ${iv.candidate_name}\nVaga: ${iv.job_title}` +
    (iv.candidate_email ? `\nEmail: ${iv.candidate_email}` : '') +
    (iv.video?.join_url ? `\n\nLink da videochamada: ${iv.video.join_url}` : '') +
    '\n\nPlataforma Trabadju.cv'
  );
  const location = encodeURIComponent(iv.video?.join_url || 'Videochamada Trabadju');
  return `https://calendar.google.com/calendar/r/eventedit?text=${title}&dates=${fmt8(start)}/${fmt8(end)}&details=${details}&location=${location}&crm=BUSY`;
}

const STATUS_STYLE = {
  proposed: { pill: 'text-amber-800 bg-amber-50 border-amber-200', stripe: 'bg-amber-400', Icon: Clock },
  scheduled: { pill: 'text-blue-800 bg-blue-50 border-blue-200', stripe: 'bg-blue-500', Icon: Video },
  completed: { pill: 'text-emerald-800 bg-emerald-50 border-emerald-200', stripe: 'bg-emerald-500', Icon: CheckCircle2 },
  needs_reschedule: { pill: 'text-orange-800 bg-orange-50 border-orange-200', stripe: 'bg-orange-400', Icon: RefreshCw },
  cancelled: { pill: 'text-gray-700 bg-gray-50 border-gray-200', stripe: 'bg-gray-300', Icon: Ban }
};
const OUTCOME_STYLE = { hired: 'bg-emerald-600 text-white', evaluating: 'bg-blue-600 text-white', rejected: 'bg-red-600 text-white' };
const FILTERS = ['all', 'proposed', 'scheduled', 'completed', 'needs_reschedule'];

export default function EmployerInterviews() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try { const { data } = await interviewsAPI.listEmployer(); setItems(data); }
    catch (e) { setError(e?.response?.data?.detail || t('interviews.errLoad')); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const counts = useMemo(() => FILTERS.reduce((acc, f) => ({ ...acc, [f]: f === 'all' ? items.length : items.filter((i) => i.status === f).length }), {}), [items]);
  const filtered = filter === 'all' ? items : items.filter((i) => i.status === filter);
  const patch = (id, data) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...data } : i)));

  return (
    <main className="min-h-screen bg-gray-50 py-8" data-testid="employer-interviews-page">
      <Helmet><title>{`${t('interviews.pageTitle')} · Trabadju`}</title></Helmet>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4 -ml-2 text-gray-600" data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-2" />{t('interviews.back')}
        </Button>
        <header className="mb-5">
          <p className="text-xs font-semibold tracking-widest text-emerald-700 uppercase">{t('interviews.eyebrow')}</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">{t('interviews.pageTitle')}</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">{t('interviews.pageSubtitle')}</p>
        </header>
        {user?.role === 'admin' && <ScheduleAudit />}

        <Card className="p-2 mb-5 border-gray-200" data-testid="interviews-filter-bar">
          <div className="flex items-center gap-1 overflow-x-auto">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`interviews-filter-${f}`}
                className={`flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${filter === f ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}>
                <span>{t(`interviews.status.${f}`)}</span>
                <span className={`text-xs px-1.5 min-w-[20px] h-5 rounded-full font-semibold inline-flex items-center justify-center ${filter === f ? 'bg-white/20' : 'bg-gray-100 text-gray-700'}`}>{counts[f]}</span>
              </button>
            ))}
          </div>
        </Card>

        {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

        {loading ? <div className="text-center py-12 text-gray-500">{t('interviews.loading')}</div>
          : filtered.length === 0 ? (
            <Card className="p-12 text-center border-dashed" data-testid="interviews-empty">
              <CalendarClock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('interviews.emptyTitle')}</h3>
              <p className="text-gray-600 mb-4">{t('interviews.emptyBody')}</p>
              <Button variant="outline" onClick={() => navigate('/applications')} data-testid="go-applications">{t('interviews.goApplications')}</Button>
            </Card>
          ) : (
            <ul className="space-y-3" data-testid="interviews-list">
              {filtered.map((iv) => <InterviewCard key={iv.id} iv={iv} t={t} lang={i18n.language} onPatch={patch} />)}
            </ul>
          )}
      </div>
    </main>
  );
}

function InterviewCard({ iv, t, lang, onPatch }) {
  const cfg = STATUS_STYLE[iv.status] || STATUS_STYLE.proposed;
  const Icon = cfg.Icon;
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState(false);
  const join = iv.video?.join_url;

  const setOutcome = async (outcome) => {
    setBusy(outcome);
    try { const { data } = await interviewsAPI.setOutcome(iv.id, outcome, note || null); onPatch(iv.id, data); }
    catch (e) { alert(e?.response?.data?.detail || t('interviews.errUpdate')); }
    finally { setBusy(''); }
  };
  const cancel = async () => {
    if (!window.confirm(t('interviews.confirmCancel'))) return;
    setBusy('cancel');
    try { const { data } = await interviewsAPI.cancel(iv.id); onPatch(iv.id, data); }
    catch (e) { alert(e?.response?.data?.detail || t('interviews.errUpdate')); }
    finally { setBusy(''); }
  };
  const copy = () => { navigator.clipboard?.writeText(join); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <li className="contents">
      <Card className="relative overflow-hidden border-gray-200" data-testid={`interview-card-${iv.id}`}>
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${cfg.stripe}`} />
        <div className="p-5 pl-6">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border ${cfg.pill}`} data-testid={`interview-status-${iv.id}`}>
              <Icon className="h-3 w-3" />{t(`interviews.status.${iv.status}`)}
            </span>
            {iv.outcome && <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${OUTCOME_STYLE[iv.outcome]}`} data-testid={`interview-outcome-${iv.id}`}>{t(`interviews.outcome.${iv.outcome}`)}</span>}
          </div>
          <h2 className="text-lg font-bold text-gray-900">{iv.candidate_name} <span className="text-gray-400 font-normal">·</span> <span className="text-gray-700 font-semibold">{iv.job_title}</span></h2>
          {iv.candidate_email && <p className="text-sm text-gray-500">{iv.candidate_email}</p>}

          <div className="mt-3 text-sm text-gray-700 space-y-1">
            {iv.status === 'proposed' && (
              <p><span className="font-semibold">{t('interviews.proposedSlots')}:</span> {iv.slots.map((s) => fmt(s.start_at, lang)).join(' · ')} <span className="text-gray-400">(hora de Cabo Verde · {iv.duration_min} min)</span></p>
            )}
            {iv.scheduled_start && (
              <p className="inline-flex items-center gap-1.5 font-semibold text-blue-900" data-testid={`interview-when-${iv.id}`}>
                <CalendarClock className="h-4 w-4" />{fmt(iv.scheduled_start, lang)} <span className="text-gray-400 font-normal">(hora de Cabo Verde · {iv.duration_min} min)</span>
              </p>
            )}
            {iv.status === 'needs_reschedule' && <p className="text-orange-800">{t('interviews.needsRescheduleHint')}{iv.declined_reason ? ` — “${iv.declined_reason}”` : ''}</p>}
            {join && (
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <a href={join} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-blue-700" data-testid={`interview-join-${iv.id}`}>
                  <Video className="h-3.5 w-3.5" />{t('interviews.join')} ({iv.video.provider === 'zoom' ? 'Zoom' : 'Jitsi'})<ExternalLink className="h-3 w-3" />
                </a>
                <button onClick={copy} className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900" data-testid={`interview-copy-${iv.id}`}>
                  <Copy className="h-3.5 w-3.5" />{copied ? t('interviews.copied') : t('interviews.copyLink')}
                </button>
                {iv.video.password && <span className="text-xs text-gray-500">{t('interviews.password')}: <strong>{iv.video.password}</strong></span>}
                {googleCalendarUrl(iv) && (
                  <a
                    href={googleCalendarUrl(iv)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md bg-white border border-slate-300 text-slate-700 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                    data-testid={`interview-gcal-${iv.id}`}
                  >
                    <CalendarPlus className="h-3.5 w-3.5 text-blue-600" />
                    Google Calendar
                  </a>
                )}
              </div>
            )}
          </div>

          {iv.status === 'scheduled' && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              <p className="text-sm font-semibold text-gray-800 mb-2">{t('interviews.outcomeTitle')}</p>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('interviews.outcomeNote')} data-testid={`outcome-note-${iv.id}`} className="mb-2" />
              <div className="flex flex-wrap gap-2 justify-end">
                <Button size="sm" variant="outline" disabled={!!busy} onClick={() => setOutcome('rejected')} className="border-red-200 text-red-700 hover:bg-red-50" data-testid={`outcome-rejected-${iv.id}`}><XCircle className="h-4 w-4 mr-1.5" />{t('interviews.outcome.rejected')}</Button>
                <Button size="sm" variant="outline" disabled={!!busy} onClick={() => setOutcome('evaluating')} className="border-blue-200 text-blue-700 hover:bg-blue-50" data-testid={`outcome-evaluating-${iv.id}`}><Hourglass className="h-4 w-4 mr-1.5" />{t('interviews.outcome.evaluating')}</Button>
                <Button size="sm" disabled={!!busy} onClick={() => setOutcome('hired')} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`outcome-hired-${iv.id}`}><CheckCircle2 className="h-4 w-4 mr-1.5" />{t('interviews.outcome.hired')}</Button>
              </div>
            </div>
          )}
          {iv.status === 'completed' && iv.outcome_note && <p className="mt-3 text-sm text-gray-600 bg-gray-50 border border-gray-100 rounded-md px-3 py-2 italic">“{iv.outcome_note}”</p>}
          {(iv.status === 'proposed' || iv.status === 'scheduled') && (
            <div className="mt-3 flex justify-end">
              <button onClick={cancel} disabled={!!busy} className="text-xs text-gray-500 hover:text-red-700 underline" data-testid={`interview-cancel-${iv.id}`}>{t('interviews.cancel')}</button>
            </div>
          )}
        </div>
      </Card>
    </li>
  );
}
